
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Sparkles, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface AITitleButtonProps {
  description: string;
  category: string | number;
  subcategory: string | number;
  condition: string;
  onGeneratedTitle: (title: string) => void;
}

const AITitleButton: React.FC<AITitleButtonProps> = ({
  description,
  category,
  subcategory,
  condition,
  onGeneratedTitle
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  
  const generateTitle = async () => {
    if (!description) {
      toast({
        title: "Mungon përshkrimi",
        description: "Ju lutemi shkruani një përshkrim para se të gjeneroni titullin SEO",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsGenerating(true);
      
      // Get category and subcategory names
      let categoryName = String(category);
      let subcategoryName = String(subcategory);
      
      try {
        // Only make these queries if we have numeric IDs
        const categoryId = Number(category);
        const subcategoryId = Number(subcategory);
        
        if (!isNaN(categoryId)) {
          const { data: categoryData } = await supabase
            .from('categories')
            .select('name')
            .eq('id', categoryId)
            .single();
            
          if (categoryData) categoryName = categoryData.name;
        }
        
        if (!isNaN(subcategoryId)) {
          const { data: subcategoryData } = await supabase
            .from('subcategories')
            .select('name')
            .eq('id', subcategoryId)
            .single();
            
          if (subcategoryData) subcategoryName = subcategoryData.name;
        }
      } catch (error) {
        console.error("Error fetching category/subcategory names:", error);
      }

      // Extract potential brand/model from description
      const extractBrandFromDescription = (desc: string) => {
        const brands = ['iPhone', 'Samsung', 'BMW', 'Mercedes', 'Audi', 'Sony', 'LG', 'Apple', 'Dell', 'HP', 'Lenovo'];
        const words = desc.split(' ');
        return brands.find(brand => words.some(word => word.toLowerCase().includes(brand.toLowerCase()))) || '';
      };

      const detectedBrand = extractBrandFromDescription(description);
      
      const { data, error } = await supabase.functions.invoke('generate-listing-title', {
        body: {
          description,
          category: categoryName,
          subcategory: subcategoryName,
          condition,
          brand: detectedBrand,
          location: 'Shqipëri', // Default location, could be made dynamic later
          language: 'sq'
        }
      });
      
      if (error) {
        throw new Error(`Gabim gjatë gjenerimit të titullit SEO: ${error.message}`);
      }
      
      if (data?.title) {
        onGeneratedTitle(data.title);
        toast({
          title: "Titulli SEO u gjenerua",
          description: "Titulli i optimizuar për motorët e kërkimit u krijua me sukses"
        });
      } else {
        throw new Error("Asnjë titull nuk u kthye nga AI");
      }
    } catch (error) {
      console.error("Gabim gjatë gjenerimit të titullit SEO:", error);
      toast({
        title: "Gabim",
        description: error.message || "Gabim gjatë gjenerimit të titullit SEO",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <Button 
      type="button" 
      variant="outline" 
      onClick={generateTitle} 
      disabled={isGenerating || !description}
      className="mt-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 hover:bg-gradient-to-r hover:from-blue-100 hover:to-indigo-100 w-full"
    >
      {isGenerating ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Duke gjeneruar titull SEO...
        </>
      ) : (
        <>
          <Sparkles className="mr-2 h-4 w-4 text-blue-500" />
          Gjenero titull SEO me AI
        </>
      )}
    </Button>
  );
};

export default AITitleButton;
